import { Fragment, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, Checkbox, FormGroup, Input, LabelPlacements, PageHeader, Select } from "@cofano/cck";
import { FormGroupOverflow } from "@cofano/cck/FormGroup/utils";

import { CircularProgress, Grid2 as Grid } from "@mui/material";

import FormField from "components/cck/FormField";
import Helmet from "components/helmet/Helmet";
import CallTypeSelect from "components/selects/CallTypeSelect";
import { ApplicationLanguageSelectOption, GET_APPLICATION_LANGUAGES_QUERY } from "components/selects/ApplicationLanguageSelect";

import { hasPluginPermission, Language } from "imports/Utils";

import StatusPaginatedSelect from "view/statuses/components/selects/StatusPaginatedSelect";

import StatusTypeSelect from "./components/selects/StatusTypeSelect";
import StatusActualTimeSelect from "./components/selects/StatusActualTimeSelect";
import StatusEstimateTypeSelect from "./components/selects/StatusEstimateTypeSelect";
import StatusEstimateRequirementSelect from "./components/selects/StatusEstimateRequirementSelect";

import { convertStatusFormToMutationInput, getDefaultStatusForm, getStatusFieldVisibility } from "./utils/StatusUtils";
import { StatusFormInput, StatusFormOutput, StatusFormSchema } from "./utils/schema";

import { ChildPropsFromArray } from "types/commons";
import { CallType, DocumentTarget, SendDocumentType, StatusEstimateRequirement, StatusType } from "__generated__/enums";
import { GetStatusDocument, GetStatusQuery, GetStatusRuleTypesDocument, GetStatusRuleTypesQuery } from "__generated__/view/statuses/graphql/queries.generated";
import { SaveStatusDocument, SaveStatusMutation } from "__generated__/view/statuses/graphql/mutations.generated";

type StatusRuleType = ChildPropsFromArray<GetStatusRuleTypesQuery, "ruleTypes">

const StatusCreateEdit = ({ statusType, setStatusType }: { statusType: StatusType, setStatusType: (type: StatusType) => void }) => {

	const { data: languageData } = useQuery(GET_APPLICATION_LANGUAGES_QUERY);
	const languages = languageData?.languages ?? [];
	const { data: statusRuleTypes } = useQuery<GetStatusRuleTypesQuery>(GetStatusRuleTypesDocument);

	return (
		<Fragment>
			{(languages && statusRuleTypes) ? (
				<Form
					languages={languages}
					statusRuleTypes={statusRuleTypes.ruleTypes}
					statusType={statusType}
					setStatusType={setStatusType} />
			) : (
				<CircularProgress size={30} />
			)}
		</Fragment>
	);
};

const Form = ({ languages, statusRuleTypes, statusType, setStatusType }: {
	languages: ApplicationLanguageSelectOption[],
	statusRuleTypes: StatusRuleType[],
	statusType: StatusType,
	setStatusType: (type: StatusType) => void,
}) => {

	const navigate = useNavigate();
	const { id } = useParams();
	const session = useStoreState(state => state.session);

	const [ saveStatus, { loading } ] = useMutation<SaveStatusMutation>(SaveStatusDocument);

	const { control, handleSubmit, formState, watch, reset, setValue } = useForm<StatusFormInput, unknown, StatusFormOutput>({
		resolver: zodResolver(StatusFormSchema),
		defaultValues: getDefaultStatusForm(statusType, statusRuleTypes),
	});

	const [ getStatus ] = useLazyQuery<GetStatusQuery>(GetStatusDocument, {
		onCompleted: response => {
			reset(getDefaultStatusForm(response.status.type, statusRuleTypes, response.status ?? undefined));
		},
	});

	const values = watch();

	const isCargoStatus = values.type === StatusType.Cargo;
	const isCallStatus = values.type === StatusType.Call;
	const isVoyageStatus = values.type === StatusType.Voyage;
	const isDeclarationStatus = values.type === StatusType.Declaration;
	const isInvoiceStatus = values.type === StatusType.Invoice;
	const fields = getStatusFieldVisibility(values.type, session);
	const hasEstimationType = !!values.estimateType;

	const save = (data: StatusFormOutput) => {
		saveStatus({
			variables: {
				input: convertStatusFormToMutationInput(data),
			},
		}).then((res) => {
			navigate("/view/statuses", { state: { type: res.data?.saveStatus?.type } });
		});
	};

	useEffect(() => {
		setStatusType(values.type);

		const newRules = statusRuleTypes
			.filter(type => type.sourceType === values.type)
			.map(type => {
				const existingRule = values.rules?.find(rule => rule.ruleType === type.name);
				return existingRule ?? {
					id: undefined,
					ifFitsStatusFlow: false,
					ifNotAppliedBefore: false,
					targetStatus: undefined,
					ruleType: type.name,
				};
			});

		reset({
			...values,
			rules: newRules,
		});
	}, [ values.type ]);

	useEffect(() => {
		if (id) {
			getStatus({
				variables: {
					id: Number(id),
				},
			});
		}
	}, []);

	const customLabelProps = {
		labelPlacement: LabelPlacements.LEFT_CENTER,
		labelWidth: 250,
		overflow: "visible" as FormGroupOverflow,
	};

	const additionalActionTypes = hasPluginPermission(session, "cargoActionUsePurgingAndCleaningTypes") ? [ CallType.Cleaning, CallType.NitrogenPurging ] : [];

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("STATUS")}
					<Helmet title={intl.formatMessage({ id: "STATUS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button
						onClick={() => navigate("/view/statuses", { state: { type: values.type } })}
						variant="outlined">
						{intl.formatMessage({ id: "CANCEL" })}
					</Button>

					<Button
						loading={loading}
						type="submit">
						{intl.formatMessage({ id: "SAVE" })}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={6}>
					<div style={{ marginBottom: 16 }}>
						<Card title={Language("DETAILS")}>
							<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
								<Grid container>
									<Grid size={statusType === StatusType.Cargo ? 6 : 12} style={{ paddingRight: 12 }}>
										<Controller
											name="type"
											control={control}
											render={({ field }) => (
												<StatusTypeSelect
													value={field.value}
													onChange={option => {
														if (option) {
															field.onChange(option);
														}
													}}
													disabled={!!id} />
											)} />
									</Grid>

									{statusType === StatusType.Cargo && (
										<Grid size={6}>
											<Controller
												name="cargoActionType"
												control={control}
												render={({ field }) => (
													<CallTypeSelect
														{...field}
														additionalOptions={additionalActionTypes}
														clearable={true}
													/>
												)} />
										</Grid>
									)}
								</Grid>
							</FormGroup>

							<FormGroup label={Language("STATUSES_DESCRIPTION")}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<FormField errors={formState.errors} name={"description"}>
									<Controller
										name="description"
										control={control}
										render={({ field }) => <Input {...field} />} />
								</FormField>
							</FormGroup>

							{languages.map((language, index) => (
								<FormGroup
									key={index}
									label={Language(`STATUSES_${language.description.toUpperCase()}`)}
									labelPlacement="left-center"
									overflow="hidden">
									<Controller
										name={`translations.descriptions.${language.code.toLowerCase()}`}
										control={control}
										render={({ field }) => <Input {...field} value={field.value ?? undefined} />} />
								</FormGroup>
							))}

							<FormGroup label={Language("COLOR")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="color"
									control={control}
									render={({ field }) => <Input type="color" {...field} />} />
							</FormGroup>

							{(isCargoStatus || isCallStatus || isVoyageStatus) && (

								<FormGroup
									label={Language("ACTUAL_TIME")}
									labelPlacement="left-center"
									overflow="hidden"
									required
								>
									<Controller
										name="requireActualTime"
										control={control}
										render={({ field }) => <StatusActualTimeSelect {...field} type={values.type} />} />
								</FormGroup>
							)}

							{fields.estimateType && (
								<FormGroup
									label={Language("ESTIMATED_TIME")}
									labelPlacement="left-center"
									overflow="hidden"
								>
									<Grid container spacing={2}>
										<Grid size={hasEstimationType ? 6 : 12}>
											<Controller
												name="estimateType"
												control={control}
												render={({ field }) =>
													<StatusEstimateTypeSelect
														{...field}
														type={values.type}
														cargoActionType={values.cargoActionType ?? null}
														onChange={(option) => {
															field.onChange(option);
															if (!option) {
																setValue("estimateRequirement", StatusEstimateRequirement.Optional);
															}
														}}
													/>}
											/>
										</Grid>
										{hasEstimationType && (
											<Grid size={6}>
												<Controller
													name="estimateRequirement"
													control={control}
													render={({ field }) => <StatusEstimateRequirementSelect {...field} />}
												/>
											</Grid>
										)}
									</Grid>
								</FormGroup>
							)}
						</Card>
					</div>

					<Card title={intl.formatMessage({ id: "ENABLED_OR_DISABLED" })}>
						<FormGroup label={intl.formatMessage({ id: "STATUSES_MEMO" })} {...customLabelProps}>
							<Controller
								name="allowComment"
								control={control}
								render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
						</FormGroup>

						{fields.allowSkipperComment && (
							<FormGroup label={intl.formatMessage({ id: "ALLOW_SKIPPER_COMMENT" })} {...customLabelProps}>
								<Controller
									name="allowSkipperComment"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}

						{fields.isInProgress && (
							<FormGroup label={Language("STATUS_IN_PROGRESS")} {...customLabelProps}>
								<Controller
									name="isInProgress"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.finishedStatus && (
							<FormGroup label={Language("STATUSES_FINISHED_STATUS")} {...customLabelProps}>
								<Controller
									name="isFinishedStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.closedStatus && (
							<FormGroup label={Language("STATUSES_CLOSED_STATUS")} {...customLabelProps}>
								<Controller
									name="isClosedStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.cancelledStatus && (
							<FormGroup label={Language("STATUSES_CANCELLED_STATUS")} {...customLabelProps}>
								<Controller
									name="isCancelledStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.expiringStatus && (
							<FormGroup label={Language("STATUSES_EXPIRING_STATUS")} {...customLabelProps}>
								<Controller
									name="isExpiringStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.applyByCustomer && (
							<FormGroup label={Language("STATUS_APPLY_BY_CUSTOMER")} {...customLabelProps}>
								<Controller
									name="applyByCustomer"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.applyBySkipper && (
							<FormGroup label={Language("STATUS_APPLY_BY_SKIPPER")} {...customLabelProps}>
								<Controller
									name="applyBySkipper"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.applyByTerminal && (
							<FormGroup label={Language("STATUS_APPLY_BY_TERMINAL")} {...customLabelProps}>
								<Controller
									name="applyByTerminal"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.loadedStatus && (
							<FormGroup label={Language("STATUSES_LOADED_STATUS")} {...customLabelProps}>
								<Controller
									name="isLoadedStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.dischargedStatus && (
							<FormGroup label={Language("STATUSES_DISCHARGED_STATUS")} {...customLabelProps}>
								<Controller
									name="isDischargedStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.voyageVisibleBySkipper && (
							<FormGroup label={Language("VOYAGE_VISIBLE_FOR_SKIPPER")} {...customLabelProps}>
								<Controller
									name="voyageVisibleBySkipper"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.voyageVisibleByCreditor && (
							<FormGroup label={Language("VOYAGE_VISIBLE_FOR_CREDITOR")} {...customLabelProps}>
								<Controller
									name="voyageVisibleByCreditor"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.voyageEditableBySkipper && (
							<FormGroup label={Language("VOYAGE_EDITABLE_BY_SKIPPER")} {...customLabelProps}>
								<Controller
									name="voyageEditableBySkipper"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.voyageEditableByCreditor && (
							<FormGroup label={Language("VOYAGE_EDITABLE_BY_CREDITOR")} {...customLabelProps}>
								<Controller
									name="voyageEditableByCreditor"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.callVisibleByLocationOwner && (
							<FormGroup label={Language("CALL_VISABLE_FOR_LOCATION_OWNER")} {...customLabelProps}>
								<Controller
									name="callVisibleByLocationOwner"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.isRejectStatus && (
							<FormGroup label={Language("VOYAGE_REFUSE")} {...customLabelProps}>
								<Controller
									name="isRejectStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.isAcceptStatus && (
							<FormGroup label={Language("VOYAGE_ACCEPTED")} {...customLabelProps}>
								<Controller
									name="isAcceptStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.notifyShipOwners && (
							<FormGroup label={Language("STATUS_NOTIFY_SHIP_OWNERS")} {...customLabelProps}>
								<Controller
									name="notifyShipOwners"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.notifySkippers && (
							<FormGroup label={Language("STATUS_NOTIFY_SKIPPERS")} {...customLabelProps}>
								<Controller
									name="notifySkippers"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.notifyShipManagers && (
							<FormGroup label={Language("STATUS_NOTIFY_SHIP_MANAGERS")} {...customLabelProps}>
								<Controller
									name="notifyShipManagers"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.notifyOperators && (
							<FormGroup label={Language("STATUS_NOTIFY_OPERATORS")} {...customLabelProps}>
								<Controller
									name="notifyOperators"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.notifyCustomers && (
							<FormGroup label={Language("STATUS_NOTIFY_CUSTOMERS")} {...customLabelProps}>
								<Controller
									name="notifyCustomers"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.notifyTerminals && (
							<FormGroup label={Language("STATUS_NOTIFY_TERMINALS")} {...customLabelProps}>
								<Controller
									name="notifyTerminals"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.isCustomerNominatingStatus && (
							<FormGroup label={Language("NOMINATE_TO_CUSTOMER")} {...customLabelProps}>
								<Controller
									name="isCustomerNominatingStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.isSkipperNominatingStatus && (
							<FormGroup label={Language("NOMINATE_TO_SKIPPER")} {...customLabelProps}>
								<Controller
									name="isSkipperNominatingStatus"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.supplierRequired && (
							<FormGroup label={Language("STATUSES_REQUIRE_SUPPLIER")} {...customLabelProps}>
								<Controller
									name="supplierRequired"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.skipperRequiredFillInSailingReport && (
							<FormGroup label={Language("STATUS_SAILING_REPORT")} {...customLabelProps}>
								<Controller
									name="skipperRequiredFillInSailingReport"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.skipperRequiredFillInWeightVolume && (
							<FormGroup label={Language("STATUS_WEIGHT_VOLUME")} {...customLabelProps}>
								<Controller
									name="skipperRequiredFillInWeightVolume"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.shouldUpdateJDE && (
							<FormGroup label={Language("STATUS_SHOULD_UPDATE_JDE")} {...customLabelProps}>
								<Controller
									name="shouldUpdateJDE"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.shouldLoadOnTerminal && (
							<FormGroup label={Language("STATUS_SHOULD_LOAD_ON_TERMINAL")} {...customLabelProps}>
								<Controller
									name="shouldLoadOnTerminal"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.laytimeStartStatus && (
							<FormGroup label={Language("STATUS_LAYTIME_START")} {...customLabelProps}>
								<Controller
									name="laytimeStart"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.laytimeEndStatus && (
							<FormGroup label={Language("STATUS_LAYTIME_END")} {...customLabelProps}>
								<Controller
									name="laytimeEnd"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.requireDocuments && (
							<FormGroup label={Language("STATUS_REQUIRE_DOCUMENTS")} {...customLabelProps}>
								<Controller
									name="requireDocuments"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
						{fields.requireTanks && (
							<FormGroup label={Language("STATUS_REQUIRE_TANKS")} {...customLabelProps}>
								<Controller
									name="requireTanks"
									control={control}
									render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
							</FormGroup>
						)}
					</Card>
				</Grid>

				<Grid size={6}>
					{hasPluginPermission(session, "statusRules") && (isCargoStatus || isCallStatus || isVoyageStatus || isDeclarationStatus || isInvoiceStatus) && (
						<div style={{ marginBottom: 16 }}>
							<Card title={Language("STATUSRULES")}>
								{[ ...statusRuleTypes ].filter(type => type.sourceType === values.type).map((type, index) => (
									<Fragment key={index}>
										<h3 style={{ fontSize: 14, margin: 0 }}>{Language(`STATUSRULE_${type.name}`)}</h3>

										<div style={{ display: "none" }}>
											<Controller
												name={`rules.${index}.id`}
												control={control}
												render={({ field }) => <Input {...field} value={field.value ?? undefined} type="hidden" />} />
										</div>

										<div style={{ marginBottom: 16, padding: 16 }}>
											<FormGroup label={Language("STATUS")} labelPlacement="left-center" overflow="hidden">
												<Controller
													name={`rules.${index}.targetStatus`}
													control={control}
													render={({ field }) => <StatusPaginatedSelect {...field} type={type.targetType} />} />
											</FormGroup>

											<FormGroup label={Language("STATUSRULE_IF_NOT_APPLIED_BEFORE_NOTE")} labelPlacement="left-center" overflow="visible">
												<Controller
													name={`rules.${index}.ifNotAppliedBefore`}
													control={control}
													render={({ field }) => (
														<Checkbox {...field} value={undefined} checked={field.value} />
													)}
												/>
											</FormGroup>

											<FormGroup label={Language("STATUSRULE_IF_FITS_STATUS_FLOW_NOTE")} labelPlacement="left-center" overflow="visible">
												<Controller
													name={`rules.${index}.ifFitsStatusFlow`}
													control={control}
													render={({ field }) => (
														<Checkbox {...field} value={undefined} checked={field.value} />
													)}
												/>
											</FormGroup>
										</div>
									</Fragment>
								))}
							</Card>
						</div>
					)}

					{hasPluginPermission(session, "successiveStatuses") && (
						<div style={{ marginBottom: 16 }}>
							<Card title={Language("STATUSES_NEXT")}>
								<Controller
									name="successiveStatuses"
									control={control}
									render={({ field }) => (
										<StatusPaginatedSelect
											{...field}
											includeActionStatus={false}
											type={values.type}
											multiple={true} />
									)} />
							</Card>
						</div>
					)}

					{<div style={{ marginBottom: 16 }}>
						<Card title={Language("STATUS_SHOW_CONFIRM_MESSAGE")}>
							<FormGroup label={Language("STATUS_SHOW_CONFIRM_MESSAGE")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="showConfirmMessage"
									control={control}
									render={({ field }) => (
										<Checkbox {...field} value={undefined} checked={field.value} />
									)}
								/>
							</FormGroup>

							{languages.map((language, index) => (
								<FormGroup
									key={index}
									label={Language(`STATUS_CONFIRM_MESSAGE_${language.code.toUpperCase()}`)}
									labelPlacement="left-center"
									overflow="hidden">
									<Controller
										name={`confirmWindowTranslations.descriptions.${language.code.toLowerCase()}`}
										control={control}
										render={({ field }) => <Input {...field} value={field.value ?? undefined} />} />
								</FormGroup>
							))}
						</Card>
					</div>}

					{(isCargoStatus) && <div style={{ marginBottom: 16 }}>
						<Card title={Language("STATUS_SEND_DOCUMENT")}>
							<FormGroup label={Language("STATUS_SEND_DOCUMENT")} labelPlacement="left-center" overflow="hidden">
								<Controller
									name="sendDocument.enabled"
									control={control}
									render={({ field }) => (
										<Checkbox {...field} value={undefined} checked={field.value} />
									)}
								/>
							</FormGroup>

							{(values.sendDocument?.enabled) && (
								<Fragment>
									<FormGroup label={Language("STATUS_SEND_DOCUMENT_TYPE")} labelPlacement="left-center" overflow="hidden">
										<Controller
											name="sendDocument.type"
											control={control}
											render={({ field }) => (
												<Select
													{...field}
													getOptionLabel={option => intl.formatMessage({ id: option })}
													fetchOptions={[ SendDocumentType.ReleaseNotice ]} />
											)} />
									</FormGroup>

									<FormGroup label={Language("STATUS_SEND_DOCUMENT_TARGET")} labelPlacement="left-center" overflow="hidden">
										<Controller
											name="sendDocument.target"
											control={control}
											render={({ field }) => (
												<Select
													{...field}
													getOptionLabel={option => intl.formatMessage({ id: option })}
													fetchOptions={[ DocumentTarget.Terminal ]} />
											)} />
									</FormGroup>
								</Fragment>
							)}
						</Card>
					</div>
					}
				</Grid>
			</Grid>
		</form>
	);
};

export default StatusCreateEdit;
